import { useCallback, useMemo, useState, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import type { ChangeRequestListFilters, ChangeRequestSortOrder, SortDirection } from "~/features/changeRequests/types";
import { v2Route } from "~/v2/routes";
import { ChangeRequestsPresenter } from "./changeRequests.presenter";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT: ChangeRequestSortOrder = "updatedAt";
const DEFAULT_DIRECTION: SortDirection = "desc";

const splitIds = (value: string | undefined): string[] => (value ? value.split(",").filter((id) => id.length > 0) : []);

export function ChangeRequestsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.changeRequests);
  const [isPending, startTransition] = useTransition();

  const sortOrder: ChangeRequestSortOrder = params.sortOrder ?? DEFAULT_SORT;
  const direction: SortDirection = params.sortOrderDirection ?? DEFAULT_DIRECTION;
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

  const filters: ChangeRequestListFilters = useMemo(
    () => ({
      search: params.search,
      statuses: splitIds(params.status),
      modelTypes: splitIds(params.modelType),
      hasReviewComments: params.hasReviewComments ?? false,
      categoryIds: splitIds(params.categoryId),
      dataImportProjectIds: splitIds(params.dataImportProjectId),
      recordComposerDefinitionId: params.recordComposerDefinitionId,
      batchChangeRequestsJobId: params.batchChangeRequestsJobId,
      batchChangeRequestsJobRunId: params.batchChangeRequestsJobRunId,
    }),
    [
      params.search,
      params.status,
      params.modelType,
      params.hasReviewComments,
      params.categoryId,
      params.dataImportProjectId,
      params.recordComposerDefinitionId,
      params.batchChangeRequestsJobId,
      params.batchChangeRequestsJobRunId,
    ],
  );

  const onApplyFilters = useCallback(
    (next: ChangeRequestListFilters) => {
      setParams((prev) => ({
        ...prev,
        search: next.search || undefined,
        status: next.statuses.length ? next.statuses.join(",") : undefined,
        modelType: next.modelTypes.length ? next.modelTypes.join(",") : undefined,
        hasReviewComments: next.hasReviewComments || undefined,
        categoryId: next.categoryIds.length ? next.categoryIds.join(",") : undefined,
        dataImportProjectId: next.dataImportProjectIds.length ? next.dataImportProjectIds.join(",") : undefined,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onSortingChange = useCallback(
    (nextOrder: ChangeRequestSortOrder, nextDirection: SortDirection) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          sortOrder: nextOrder === DEFAULT_SORT ? undefined : nextOrder,
          sortOrderDirection: nextDirection === DEFAULT_DIRECTION ? undefined : nextDirection,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
      });
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next, page: undefined }));
      });
    },
    [setParams],
  );

  const selectedChangeRequest = params.selectedChangeRequest ?? null;
  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedChangeRequest: id })),
    [setParams],
  );
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedChangeRequest: undefined })),
    [setParams],
  );

  return (
    <ChangeRequestsPresenter
      filters={filters}
      onApplyFilters={onApplyFilters}
      sortOrder={sortOrder}
      direction={direction}
      onSortingChange={onSortingChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      selectedChangeRequest={selectedChangeRequest}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
